// Studio app — root. CD owns the whole app (shell + screens); this is the
// deployable frontend. Data comes from @v0y4ge/api-client; design system from
// @v0y4ge/jrni-ui + jrni-tokens (CD-owned). No static seed data in the render path.

import "./styles/platform-layers.css";

// App-layout rules (route-shell max-width override) — deliberately OUTSIDE the
// token stylesheets so DS re-emits can never delete them (DS round-trip ①).
import "./styles/studio-app.css";
import { useEffect, useRef } from "react";
import { NavProvider, useNav, isPublicAttendRoute, isPublicHost } from "./shell/router";
import { AppRouter } from "./shell/AppRouter";
import { ToastHost } from "./shell/toast";
import { useAuth, signIn, landingResolved, getUser, expireSession } from "./shell/access";
import { onSessionExpired } from "@v0y4ge/api-client";
import { navigationGuardActive, isFormRoute } from "./shell/navigation-guard";
import { resolveLanding } from "./shell/navProfiles";
import { ensureTenantContext } from "./lib/tenant-vocab";
import Signin from "./shell/Signin";
import { ImpersonationBanner } from "./shell/ImpersonationBanner";
import { useDateTimeVersion } from "./lib/datetime";
import { useStaffTimezoneSync } from "./screens/staff/useStaffTimezone";
import { ErrorBoundary } from "./shell/ErrorBoundary";
import { PublicConsentHost } from "./lib/PublicConsentHost";
import { ReportProblemHost } from "./support/ReportProblem";

import { useT } from "@v0y4ge/i18n-runtime";
import { loadPublicWorkspaceDateTime, loadStudioWorkspaceDateTime } from "./lib/workspace-datetime";
// Deep-link params captured at MODULE LOAD — before <NavProvider> mounts and
// rewrites the URL to a route (which would drop our fragment). Read from the
// hash (survives the preview host's path rewrite) with a query fallback.
const DEEPLINK: { persona: string; route: string | null } | null = (function () {
  if (typeof window === "undefined") return null;
  const raw = (window.location.hash || "").replace(/^#/, "") || (window.location.search || "").replace(/^\?/, "");
  const q = new URLSearchParams(raw);
  const persona = q.get("persona");
  return persona ? { persona, route: q.get("route") } : null;
})();

/** The host shell's sign-in reachability (CC 0191/0194): App → AuthGate → Signin. */
export interface StudioAppProps {
  demoUnreachable?: boolean;
  onRetry?: () => void | Promise<unknown>;
}

function AuthGate({ demoUnreachable, onRetry }: StudioAppProps) {
  // Re-render the whole screen tree when locale/timezone changes so every
  // date/time re-formats live.
  useDateTimeVersion();
  // The signed-in staff member's own profile zone becomes the display zone
  // (explicit user preference still wins) — 2026-08-07, Pat.
  useStaffTimezoneSync();
  const { signedIn, resolvingLanding, reauth, screenEpoch } = useAuth();
  const { route, go } = useNav();
  /* Expired session (CC 0176): the api-client raises ONE signal for a 401
     AUTH_REQUIRED on a Studio call; this is the only place that reacts. It keeps
     the current screen to return to and whether a form was open, then clears the
     signed-in state so <Signin/> takes over before any screen shows its own
     "Couldn't load" error. Public hosts never mark a session, so never get here. */
  const navNow = useNav();
  const whereRef = useRef({ route: navNow.route, params: navNow.params });
  whereRef.current = { route: navNow.route, params: navNow.params };
  useEffect(() => {
    if (isPublicHost() || typeof onSessionExpired !== "function") return;
    return onSessionExpired(() => {
      /* Keep the screen under the sign-in dialog for a registered guard OR any form route (CC 0336):
         most editors register no guard, and their typed work would otherwise unmount with the app. */
      expireSession({ ...whereRef.current, unsavedChanges: navigationGuardActive() || isFormRoute(whereRef.current.route) });
    });
  }, []);
  // Deep-link auto-sign-in (demo): a link like index.html?persona=<id>&route=<routeId>
  // (used by the RFE board's login tags) mints that persona's session and lands
  // on the target route, skipping the org/persona chooser. // ui-config
  // Same sequencing as Signin.pick() (2026-09-17): `signIn()` raises the
  // resolving-landing gate, the served landing is awaited, and the gate clears
  // in `finally` — so a `?persona=` link can no more flash /dashboard than a
  // card press can. An explicit `route=` still wins over the served landing.
  /* The workspace's date and time format (item 8): a public host reads it from the
     public theme once at start-up; Studio reads Settings > General once signed in. */
  useEffect(() => {
    if (isPublicHost()) { void loadPublicWorkspaceDateTime(); return; }
    if (signedIn) void loadStudioWorkspaceDateTime();
  }, [signedIn]);
  const booted = useRef(false);
  useEffect(() => {
    /* While sign-in is unreachable (CC 0210) a persona link waits: it is kept, and
       acted on only once onRetry succeeds and demoUnreachable turns false. */
    if (booted.current || !DEEPLINK || signedIn || demoUnreachable) return;
    booted.current = true;
    (async () => {
      try {
        await signIn(DEEPLINK.persona);
        void ensureTenantContext();
        const target = DEEPLINK.route || (await resolveLanding(getUser())).route;
        go(target);
      } catch { /* stay on the chooser */ }
      finally { landingResolved(); }
    })();
  }, [signedIn, go, demoUnreachable]);
  // 2026-07-14 docket (attend-host flip): a public event route on the attend
  // host renders ANONYMOUSLY — no session, no persona chooser. attend.<tenant>
  // is the public WYSIWYG face of a published event; studio.<tenant> (and every
  // operator route on any host) stays SSO-gated exactly as before.
  // 2026-08-11 (JAI-562): decide on the HOST FIRST. The check below used to be
  // host-AND-route, which fails OPEN: on a bare `attend.<tenant>/` the path is
  // empty, pathToEntry() falls back to "dashboard", that is not in
  // PUBLIC_ATTEND_ROUTES, and control dropped through to <Signin/> — so a
  // public hostname rendered the operator persona chooser. Worse, picking a
  // persona flipped `signedIn` and handed back the full operator <AppRouter/>
  // on that same public host.
  //
  // The four sibling public hosts (book./join./meet./public.) were never
  // covered at all, because isAttendHost() matches only `attend` — they showed
  // the chooser on EVERY path. They have no public surface yet, so they now
  // correctly render the public not-found instead of a way in.
  //
  // Host-first makes the failure mode closed: on a public host the only thing
  // reachable is an allowlisted public route. Operator hosts are untouched.
  /* Public pages carry the analytics-consent banner and keep the first/last-touch history (lib/public-touch.ts). */
  if (isPublicHost()) return isPublicAttendRoute(route) ? <><AppRouter /><PublicConsentHost route={route} /></> : <PublicNotFound />;
  /* `resolvingLanding` (2026-09-17): signed in, but the landing route is not yet
     applied. Rendering the router here mounted Dashboard at the boot default —
     the ~20 admin fetches, the two permission banners and the phone launcher's
     "No apps are enabled" flash all came from this one line. Signin stays
     mounted and shows the step overlay until nav.reset() has fired. */
  /* Re-sign-in over the screen (CC 0306): with unsaved changes the screen stays mounted and sign-in
     opens over it, also while the new session resolves. */
  if (!reauth && (!signedIn || resolvingLanding)) return <Signin demoUnreachable={demoUnreachable} onRetry={onRetry} />;
  /* The acting-as banner mounts HERE, at the app root, not in `Screen`.
     Mounted in `Screen` it missed every view that doesn't use it — including
     the app launcher's "No apps are enabled for your role yet" shell, which is
     exactly where an impersonated narrow-role persona lands. The admin was left
     with no banner and no exit but Sign out, which is the whole bug this was
     meant to fix. The narrower the target's role, the more certain the banner
     was to be missing. */
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <ImpersonationBanner />
      <div style={{ flex: 1, minHeight: 0 }} aria-hidden={reauth ? true : undefined} {...(reauth ? ({ inert: "" } as Record<string, string>) : {})}><AppRouter key={screenEpoch} /></div>
      {reauth ? (
        <div role="dialog" aria-modal="true" aria-label="Sign in again" data-testid="reauth-dialog" style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(17,24,39,.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ width: "100%", maxWidth: 940, height: "min(92vh, 860px)", borderRadius: 16, overflow: "hidden", boxShadow: "0 24px 64px rgba(17,24,39,.28)" }}>
            <Signin mode="reauth" demoUnreachable={demoUnreachable} onRetry={onRetry} />
          </div>
        </div>
      ) : null}
      {/* Support intake (2026-09-21). Mounted INSIDE the signed-in branch on
          purpose: unmounting on sign-out is what clears the session capture
          buffer, so a shared branch terminal never hands the next person the
          previous one's route trail. */}
      <ReportProblemHost />
    </div>
  );
}

/**
 * Public 404. A visitor on a public host who asks for anything outside the
 * public allowlist gets this — deliberately content-free: no product nav, no
 * sign-in affordance, nothing that hints at an operator surface behind it.
 */
function PublicNotFound() {
  const t = useT();
  return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 40, fontFamily: "var(--jrni-font-family-sans)" }}>
      <div style={{ maxWidth: 420, textAlign: "center" }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: "var(--jrni-color-neutral-1)", marginBottom: 8 }}>{t("common.app.text", "Page not found")}</div>
        <div style={{ fontSize: 14, color: "var(--jrni-color-neutral-2)", lineHeight: 1.5 }}>{t("common.app.text2", "This page isn't available.")}</div>
      </div>
    </div>
  );
}

/**
 * §8 (2026-08-19) — the app root carries `data-theme` so dark can be RENDERED at all.
 * Until this line the app had never been drawn in dark: every token pair was in
 * `tokens.css`, and nothing set the attribute they hang off.
 *
 * It runs at MODULE level here rather than in `main.tsx` because the preview host
 * builds its own entry that imports THIS file and calls `createRoot` itself — a line in
 * main.tsx never executes there, which is how the first attempt went unnoticed.
 *
 * NO user-facing toggle in this pass, deliberately: the goal is a testable build.
 * `document.documentElement.setAttribute("data-theme", "dark")` in the console flips it,
 * and an attribute already set by a host page or a screenshot harness is left alone.
 */
if (typeof document !== "undefined" && !document.documentElement.hasAttribute("data-theme")) {
  document.documentElement.setAttribute("data-theme", "light");
}

export default function App({ demoUnreachable, onRetry }: StudioAppProps = {}) {
  const t = useT();
  return (
    <div data-jrni-component="studio-app-root" style={{ height: "100vh", overflow: "hidden", background: "var(--jrni-color-surface-canvas)" }}>
      {/* Last-resort boundary: catches anything thrown OUTSIDE a screen (the
          rail, topbar, router or auth gate itself) so the app can never render
          a bare grey page with no way back. Per-screen boundaries live in
          AppRouter and handle the common case without losing the chrome. */}
      <ErrorBoundary scope="app" label="Studio" variant="app">
        <NavProvider initialRoute="dashboard">
          <AuthGate demoUnreachable={demoUnreachable} onRetry={onRetry} />
        </NavProvider>
      </ErrorBoundary>
      <ToastHost />
    </div>
  );
}
